import { DefaultChatTransport } from 'ai';
import { AI_CHAT_MESSAGES, AI_CHAT_MESSAGES_BY_CHAT } from 'constants/requestUrls';

import appendDossierFactTypes from './appendDossierFactTypes';
import observeToolOutputs from './observeToolOutputs';

import type { ChatTransport } from 'ai';
import type IAiChatTransportOptions from '../interfaces/IAiChatTransportOptions';
import type { TAiChatUiMessage } from '../interfaces/IAiChatUiMessage';

/** The text the user just sent: the first text part of the last message in the list. */
const readSentText = (messages: TAiChatUiMessage[]): string => {
  const last = messages[messages.length - 1];
  const part = last?.parts.find((candidate) => candidate.type === 'text');

  return part && part.type === 'text' ? part.text : '';
};

/**
 * The transport `useChat` sends through: this API's send endpoints, with the request they expect.
 *
 * The SDK posts the whole message list by default. This API takes one `text` and an optional
 * `chatContext`, and reads the chat from the path, so the request is rebuilt here per send. The
 * `fetch` wrapper reports the moment the response opens: the chat list is stale from then on.
 *
 * Every option is a getter, read per request. The transport is built once per conversation, and
 * the chat, the context, and the token all change under it.
 *
 * `onToolOutput` watches the stream itself: a tool that acts on the screen must act once, on the
 * turn that called it, and the stream is the one place a part is live rather than loaded.
 */
const createAiChatTransport = ({
  apiBase, getAccessToken, getChatId, getChatContext, onResponseOpen, onToolOutput,
}: IAiChatTransportOptions): ChatTransport<TAiChatUiMessage> => {
  const transport = new DefaultChatTransport<TAiChatUiMessage>({
    headers: () => ({ authorization: `Bearer ${getAccessToken()}` }),
    prepareSendMessagesRequest: ({ messages }) => {
      const chatId = getChatId();
      const chatContext = getChatContext();
      const path = chatId
        ? AI_CHAT_MESSAGES_BY_CHAT.replace('{chatId}', `${chatId}`)
        : AI_CHAT_MESSAGES;
      const text = appendDossierFactTypes(readSentText(messages), chatContext);

      return {
        api: `${apiBase}${path}`,
        // Sent only when the caller gave one: the field is optional, and an empty object would
        // cost the turn a line of prompt for nothing.
        body: chatContext ? { text, chatContext } : { text },
      };
    },
    fetch: async (input, init) => {
      const response = await fetch(input, init);

      onResponseOpen({ isOk: response.ok });

      return response;
    },
  });

  if (!onToolOutput) {
    return transport;
  }

  return {
    sendMessages: async (options) => observeToolOutputs(await transport.sendMessages(options), onToolOutput),
    reconnectToStream: async (options) => {
      const stream = await transport.reconnectToStream(options);

      return stream && observeToolOutputs(stream, onToolOutput);
    },
  };
};

export default createAiChatTransport;
